/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./PoweredByAPI',
	'../../../app/nls/StringResources'
], (APIFactory, PoweredByAPI, StringResources) => {

	const SUPPORTED_TECH_MAP = { 'D2': 'ibmDb2' };
	class PoweredBy {
		constructor(options) {
			this._features = options.features; // for runtime dependencies
			this._canvas = options.features['Canvas'];
			this._api = APIFactory.createAPI(this, [PoweredByAPI]);
			this._poweredByMap = {};
			this._setUpEvents();
		}

		_setUpEvents() {
			// only cares about remove; adding content will be taken care by the caller (eg. render sequence)
			this._canvas.on('remove:content:child', this._onDeleteContent, this);
		}

		_onDeleteContent(payload) {
			const contentId = payload.info.value;
			if (this._poweredByMap[contentId]) {
				this.set(contentId, []);
			}
		}

		getAPI() {
			return this._api;
		}

		get(id) {
			if (id) {
				return this._poweredByMap[id];
			} else {
				return this._poweredByMap;
			}
		}

		set(contentId, technologies) {
			if (!Array.isArray(technologies)) {
				throw new Error('The second param technologies need to be an array');
			}
			const supportedTechnologies = this._filterOutUnsupportedTechnologies(technologies);
			if (supportedTechnologies.length) {
				this._poweredByMap[contentId] = supportedTechnologies;
			} else {
				delete this._poweredByMap[contentId];
			}
			this._refreshUI();
		}

		_refreshUI () {
			if (Object.keys(this._poweredByMap).length) {
				this._renderMessage();
			} else {
				this._removeMessage();
			}
		}

		_filterOutUnsupportedTechnologies(technologies) {
			const supportedTechnologies = [];
			technologies.forEach((technologyKey) => {
				if (Object.keys(SUPPORTED_TECH_MAP).includes(technologyKey)) {
					supportedTechnologies.push(technologyKey);
				}
			});
			return supportedTechnologies;
		}

		_renderMessage() {
			const overallTechnologies = [];
			for (const contentId in this._poweredByMap) {
				const widgetTechnologies = this._poweredByMap[contentId];
				widgetTechnologies.forEach(function (technologyKey) {
					if (!overallTechnologies.includes(technologyKey)) {
						overallTechnologies.push(technologyKey);
					}
				});
			}
			if (overallTechnologies.length) {
				const messageFeature = this._features['FooterMessageBox'];
				let overallTechnologiesMessage = '';
				const SEPARATOR = ', ';
				// allow other children perspective to contribute its string
				const StringResourcesSvc = this._features['API'].getDashboardCoreSvc('.StringResources');
				overallTechnologies.forEach((key) => {
					if(overallTechnologiesMessage) {
						overallTechnologiesMessage += SEPARATOR + StringResources.get(SUPPORTED_TECH_MAP[key]);
					} else {
						overallTechnologiesMessage = StringResources.get(SUPPORTED_TECH_MAP[key]);
					}
				});
				const prefix = StringResourcesSvc.get('dashboardPoweredByPrefix');
				messageFeature.setMessage(prefix, overallTechnologiesMessage);
			}
		}

		_removeMessage() {
			const messageFeature = this._features['FooterMessageBox'];
			messageFeature.setMessage();
		}

		destroy() {
			this._canvas.off('remove:content:child', this._onDeleteContent, this);
			this._canvas = null;
		}

	}

	return PoweredBy;
});
